import {
    Component,
    defineElement,
    FC,
    useBindMethod,
    useElement,
    useListen,
    useProp,
} from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { useStore } from '@atomify/kit';
import { ButtonSquare } from '@atoms/button-square/button-square';
import { virtualView3dPointerEvents } from '@features/virtual-tour/constants';
import {
    getViewInStore,
    popView,
    pushView,
    VirtualTourState,
    virtualTourStore,
} from '@features/virtual-tour/store';
import {
    VirtualTourMinimap,
    VirtualTourPointer,
    VirtualView,
    VRTourImageInterface,
    VRTourKRPanoInterface,
    VRTourPointerFilter,
} from '@features/virtual-tour/types';
import { krpanoEmittedEvents } from '@molecules/krpano/constants';
import { getPrefixedUrl } from '@utilities/get-prefixed-url';
import { useStylesheet } from '@utilities/hooks';

import style from './virtual-tour.scss';

const KRPANO_GO_BACK_HOOK = 'krpano-go-back-button';
const KRPANO_ZOOM_IN_HOOK = 'krpano-zoom-in-button';
const KRPANO_ZOOM_OUT_HOOK = 'krpano-zoom-out-button';
const MINIMAP_CONTAINER_HOOK = 'virtual-tour-minimap-container';

const VIRTUAL_TOUR_2D = 'virtual-tour--2d';
const VIRTUAL_TOUR_3D = 'virtual-tour--3d';
const VIRTUAL_TOUR_MINIMAP = 'virtual-tour--has-minimap';

const warn = (data: any) => console.warn('[virtual-tour]: Missing view data >>>> ', data);

export interface VirtualTourElement extends Component {
    focusPointer: (isHighlighted: VRTourPointerFilter) => void;
    setMinimap: (minimap: VirtualTourMinimap, currentView: VirtualView) => void;
}

const VirtualTour: FC<VirtualTourElement> = ({ element }) => {
    useStylesheet(style);
    const [onMissingData] = useProp<(sceneId: string) => void>('onMissingData', warn);
    const [onClose] = useProp<Function | null>('onClose', null);
    const [pointerHighlighted] = useProp<VRTourPointerFilter | null>('pointerHighlighted', null);

    const [vrStore, subscribe] = useStore<VirtualTourState>(virtualTourStore);

    const krPanoContainer = useElement<VRTourKRPanoInterface>('vr-tour-krpano-view');
    const imageContainer = useElement<VRTourImageInterface>('vr-tour-image-view');

    const goBackButton = useElement<HTMLButtonElement>(`[js-hook-${KRPANO_GO_BACK_HOOK}]`);
    const zoomInButton = useElement<HTMLButtonElement>(`[js-hook-${KRPANO_ZOOM_IN_HOOK}]`);
    const zoomOutButton = useElement<HTMLButtonElement>(`[js-hook-${KRPANO_ZOOM_OUT_HOOK}]`);

    const minimapContainer = useElement<HTMLButtonElement>(`[js-hook-${MINIMAP_CONTAINER_HOOK}]`);

    subscribe(
        async ({ [element.id]: { viewStack, currentView, minimap } }) => {
            if (!viewStack.length || !currentView) return;

            currentViewType = currentView.type;

            if (currentViewType === '3d') {
                element.classList.remove(VIRTUAL_TOUR_2D);
                element.classList.add(VIRTUAL_TOUR_3D);
            } else {
                element.classList.remove(VIRTUAL_TOUR_3D);
                element.classList.add(VIRTUAL_TOUR_2D);
            }
            element.classList[minimap ? 'add' : 'remove'](VIRTUAL_TOUR_MINIMAP);
            minimap && setMinimap(minimap, currentView);

            if (viewStack.length === 1) {
                goBackButton.current!.setAttribute('disabled', 'true');
            } else {
                goBackButton.current!.removeAttribute('disabled');
            }
        },
        [element.id],
    );

    function onPointerClick(pointer: VirtualTourPointer) {
        if (!pointer.sceneId) return;
        const viewInStore = getViewInStore(element.id, pointer.sceneId);
        const isSameScene = viewInStore?.id === vrStore[element.id].currentView?.id;

        if (viewInStore) {
            !isSameScene && pushView(element.id, pointer.sceneId);
            return;
        }

        onMissingData(pointer.sceneId);
    }

    useBindMethod('focusPointer', (isHighlighted: VRTourPointerFilter) => {
        imageContainer.current?.highlightPointers(isHighlighted);
    });

    let krpanoBackDisabled = true;
    let currentViewType: '2d' | '3d';

    useListen(document, virtualView3dPointerEvents.REQUEST_2D_SCENE, (e) => {
        if (e.detail.tourId !== element.id) return;
        pushView(element.id, e.detail.sceneId);
    });

    useListen(krPanoContainer, krpanoEmittedEvents.TOGGLE_GO_BACK, (e) => {
        const { viewStack } = vrStore[`${element.id}`];
        krpanoBackDisabled = e.detail.disabled;

        if (viewStack.length === 1 && krpanoBackDisabled) {
            goBackButton.current!.setAttribute('disabled', 'true');
        } else {
            goBackButton.current!.removeAttribute('disabled');
        }
    });

    useListen(krPanoContainer, krpanoEmittedEvents.TOGGLE_ZOOM_IN, (e) => {
        if (e.detail.disabled) {
            zoomInButton.current!.setAttribute('disabled', 'true');
        } else {
            zoomInButton.current!.removeAttribute('disabled');
        }
    });

    useListen(krPanoContainer, krpanoEmittedEvents.TOGGLE_ZOOM_OUT, (e) => {
        if (e.detail.disabled) {
            zoomOutButton.current!.setAttribute('disabled', 'true');
        } else {
            zoomOutButton.current!.removeAttribute('disabled');
        }
    });

    const onBack = () => {
        if (currentViewType == '2d' || krpanoBackDisabled) popView(element.id);
        else krPanoContainer.current?.goBack();
    };
    const onZoomIn = () => krPanoContainer.current?.zoomIn();
    const onZoomOut = () => krPanoContainer.current?.zoomOut();

    function setMinimap(minimap: VirtualTourMinimap, currentView: VirtualView) {
        if (!minimapContainer.current) return;
        minimapContainer.current.innerHTML = '';

        minimapContainer.current.append(
            <div class="virtual-tour__minimap" js-hook-virtual-tour-minimap>
                <img
                    src={`${getPrefixedUrl(minimap.source)}?width=270&height=150&mode=crop`}
                    alt=""
                    class="virtual-tour__minimap-image"
                />
                {minimap.pointers.map((p) => (
                    <button
                        style={{ left: `${p.position[1]}%`, top: `${p.position[0]}%` }}
                        class={`virtual-tour__minimap-pointer${
                            currentView.id === p.sceneId ? ' is--selected' : ''
                        }`}
                        onClick={() => onPointerClick(p)}
                    />
                ))}
            </div>,
        );
    }

    useBindMethod('setMinimap', setMinimap);

    return (
        <div class="c-virtual-tour">
            <vr-tour-krpano-view vr-tour-id={element.id} krpano-id={`krpano-${element.id}`} />
            <vr-tour-image-view
                vr-tour-id={element.id}
                onPointerClick={onPointerClick}
                pointerHighlighted={pointerHighlighted}
            />
            <div class="virtual-tour__navigation">
                <div
                    class="virtual-tour__minimap-container"
                    js-hook-virtual-tour-minimap-container></div>
                <div class="virtual-tour__controls" js-hook-virtual-tour-controls>
                    <ButtonSquare
                        onClick={onBack}
                        svg="icons-back"
                        ariaLabel="back"
                        hook={KRPANO_GO_BACK_HOOK}
                        disabled
                    />
                    {onClose && (
                        <ButtonSquare onClick={onClose} svg="icons-close" ariaLabel="close" />
                    )}
                    <ButtonSquare
                        onClick={onZoomIn}
                        svg="icons-plus"
                        ariaLabel="zoom in"
                        classes="virtual-tour__control--3d-only"
                        hook={KRPANO_ZOOM_IN_HOOK}
                    />
                    <ButtonSquare
                        onClick={onZoomOut}
                        svg="icons-minus"
                        ariaLabel="zoom out"
                        classes="virtual-tour__control--3d-only"
                        hook={KRPANO_ZOOM_OUT_HOOK}
                    />
                </div>
            </div>
        </div>
    );
};

VirtualTour.props = {
    onMissingData: {
        type: Function,
    },
    onClose: {
        type: Function,
    },
    pointerHighlighted: {
        type: Function,
    },
};

defineElement('virtual-tour', VirtualTour);
